import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { getFirstAllowedPage } from "../utils/pageAccess";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConflict, setShowConflict] = useState(false);

  // Show session-revoked message if redirected from forced logout
  useEffect(() => {
    const msg = sessionStorage.getItem("session_message");
    if (msg) {
      setInfo(msg);
      sessionStorage.removeItem("session_message");
    }
  }, []);

  const doLogin = async (force = false) => {
    setError("");
    setSubmitting(true);
    try {
      const userData = await login(username, password, force);
      const perms = userData.permissions || [];
      const landing = getFirstAllowedPage(perms, { isMobile }) || "/dashboard";
      navigate(landing, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail === "active_session_exists") {
        setShowConflict(true);
      } else {
        setError("Invalid username or password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await doLogin(false);
  };

  const handleForceLogin = async () => {
    setShowConflict(false);
    await doLogin(true);
  };

  const handleCancelConflict = () => {
    setShowConflict(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0f2027 0%, #1b3a5c 40%, #1a5e5a 70%, #0f2027 100%)" }}>

      {/* Animated fiber optic lines - decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Diagonal light streaks */}
        <div className="absolute w-[1px] h-[200%] bg-gradient-to-b from-transparent via-teal-400/20 to-transparent -rotate-[25deg]" style={{ left: "15%", top: "-50%", animation: "pulse 4s ease-in-out infinite" }} />
        <div className="absolute w-[1px] h-[200%] bg-gradient-to-b from-transparent via-cyan-300/15 to-transparent -rotate-[25deg]" style={{ left: "35%", top: "-50%", animation: "pulse 4s ease-in-out infinite 1s" }} />
        <div className="absolute w-[1px] h-[200%] bg-gradient-to-b from-transparent via-teal-400/20 to-transparent -rotate-[25deg]" style={{ left: "65%", top: "-50%", animation: "pulse 4s ease-in-out infinite 2s" }} />
        <div className="absolute w-[1px] h-[200%] bg-gradient-to-b from-transparent via-cyan-300/10 to-transparent -rotate-[25deg]" style={{ left: "82%", top: "-50%", animation: "pulse 4s ease-in-out infinite 0.5s" }} />

        {/* Glowing dots */}
        <div className="absolute w-2 h-2 rounded-full bg-teal-400/30" style={{ top: "20%", left: "25%", boxShadow: "0 0 12px 3px rgba(45,212,191,0.15)" }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-cyan-300/25" style={{ top: "60%", left: "75%", boxShadow: "0 0 10px 3px rgba(103,232,249,0.12)" }} />
        <div className="absolute w-1 h-1 rounded-full bg-teal-300/30" style={{ top: "80%", left: "20%", boxShadow: "0 0 8px 2px rgba(94,234,212,0.1)" }} />
        <div className="absolute w-2 h-2 rounded-full bg-cyan-400/20" style={{ top: "35%", left: "85%", boxShadow: "0 0 12px 3px rgba(34,211,238,0.1)" }} />

        {/* Subtle hexagon grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hex" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
              <path d="M28 0 L56 16.7 L56 50 L28 66.7 L0 50 L0 16.7 Z" fill="none" stroke="white" strokeWidth="0.5"/>
              <path d="M28 33.3 L56 50 L56 83.3 L28 100 L0 83.3 L0 50 Z" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hex)"/>
        </svg>
      </div>

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white/[0.97] backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1.5" style={{ background: "linear-gradient(90deg, #1B3A5C, #2A9D8F, #3BB5A8, #1B3A5C)" }} />

          <div className="px-8 pt-8 pb-10">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img src="/logo-full.png" alt="BEST Inc. - Brilliant Expert Solutions and Technologies" className="h-48 w-auto" />
            </div>

            {/* App title */}
            <p className="text-center text-sm font-medium text-gray-500 tracking-wide mb-8">
              Dispatch Monitoring System
            </p>

            {info && (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-4 py-2.5 mb-5 flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                {info}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-2.5 mb-5 flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                <input
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  spellCheck={false}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent transition-shadow"
                  placeholder="Enter your username"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full border border-gray-300 rounded-lg pl-4 pr-11 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent transition-shadow"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-gray-400 hover:text-[#2A9D8F] transition-colors"
                  >
                    {showPassword ? (
                      /* Eye-off icon (password visible → click to hide) */
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      /* Eye icon (password hidden → click to show) */
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 rounded-lg text-white font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                style={{ background: "linear-gradient(135deg, #1B3A5C, #2A9D8F)" }}
              >
                {submitting ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-white/40 mt-6">
          &copy; {new Date().getFullYear()} BEST Inc. All rights reserved.
        </p>
      </div>

      {/* Active Session Conflict Modal */}
      {showConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="h-1.5" style={{ background: "linear-gradient(90deg, #dc2626, #f59e0b, #dc2626)" }} />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Active Session Detected</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                This account is already logged in on another device or browser. If you continue, the other active session will be logged out.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelConflict}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForceLogin}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? "Signing in…" : "Continue & Log Out Other"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
